/**
 * Left workstream sidebar (MapWorkstreams.vue), x 0–160: an opaque pre-block,
 * then a white row per visible workstream with a top border line and the
 * right-aligned, bold workstream title (wrapped to the row's height, `…` when it
 * needs more lines — WI #5729); the selected workstream's row is bluelight with
 * a white title, like MapWorkstreams' `.selected`. A soft shadow fades in at the
 * sidebar edge once the scene is scrolled. Each row carries a hover-revealed Eye
 * (hide); when workstreams are hidden, a top placeholder row + gap dividers carry
 * EyeOffs to re-show them (the gap EyeOff is hover-revealed). The drag-reorder
 * affordance is still deferred.
 */
import { FAKE_WORKSTREAM_ID, START_X, START_Y } from '@/store/remapStore';
import {
  canHideWorkstream,
  EYE_SIZE,
  gapEyeCenter,
  gapLineYs,
  hiddenWorkstreamGaps,
  INFO_SIZE,
  workstreamExpandCenter,
  workstreamEyeCenter,
  workstreamInfoCenter,
} from '../../draw/hideEye';
import { drawIcon } from '../../draw/icons';
import { cssVar } from '../../draw/palette';
import { wrapText } from '../../draw/text';
import type { SceneContext } from '../../engine/SceneContext';

// WI #5550: hit padding around the drawn title text — the pointer affordance
// covers the text plus a few px, NOT the whole sidebar row.
const TITLE_HIT_PAD = 3;

const ROW_FILL = '#FFFFFF';

// WI #5729: half-extent of the backdrop behind a row icon — the glyph plus 2 px.
const ICON_BACKDROP_HALF = EYE_SIZE / 2 + 2;
// The ink of a title line (700 11px Roboto, textBaseline 'middle') around its
// centre: capitals with diacritics above (8.25 px), descenders below (4.75 px).
// Less than the 14 px line height together, so the ink of neighbouring lines
// never overlaps.
const TITLE_INK_ABOVE = 8.5;
const TITLE_INK_BELOW = 5;

/** A drawn title line, for the icon backdrops to cover whole. */
interface TitleLine {
  y: number;
  /** Left end of the right-aligned text. */
  left: number;
  /** Top of the line's row: a backdrop never grows above it. */
  rowTop: number;
}

/** A painted sidebar row, for the icon backdrops to repaint beneath them. */
interface SidebarRow {
  top: number;
  bottom: number;
  fill: string;
}

/** What an icon backdrop repaints, and the title lines it covers whole. */
interface SidebarLayout {
  rows: SidebarRow[];
  titleLines: TitleLine[];
  borderColor: string;
}

export function drawSidebar(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map, io } = scene;
  const sidebarWidth = START_X - 20; // 160, matches MapWorkstreams.

  // Opaque pre-block behind the whole sidebar header area.
  ctx.fillStyle = '#F2F4F7';
  ctx.fillRect(0, 0, sidebarWidth, START_Y);

  // Project image over the pre-block (MapWorkstreams `<image>` x0 y0
  // width=START_X-30 height=START_Y), projects only.
  drawProjectImage(ctx, scene);

  const borderColor = cssVar('--color-gray-300', '#D0D5DD');
  const textColor = cssVar('--color-gray-600', '#475467');
  const eyeColor = cssVar('--color-gray-500', '#667085');
  // MapWorkstreams' `.selected` row: bluelight-500, its title white.
  const selectedFill = cssVar('--color-bluelight-500', '#0BA5EC');
  const selectedText = cssVar('--color-gray-0', '#FFFFFF');
  const layout: SidebarLayout = { rows: [], titleLines: [], borderColor };

  // Placeholder row collapsing all hidden workstreams (MapWorkstreams' fake row),
  // sitting at the top; its EyeOff re-shows every hidden workstream. It's an
  // interactive affordance, so the PDF export omits it entirely — mirroring the
  // SVG's `v-if="... && !isExportingPDF"` (MapWorkstreams.vue).
  // WI #5596: the row also shows while filter hide-mode is hiding activities —
  // their link anchors park here — but the EyeOff (and the count it implies)
  // stays manual-only: filter-hidden content has no reveal affordance.
  if (
    (io.hiddenWorkstreamsOnMapCount > 0 || map.hasFilterHiddenActivities) &&
    !scene.exporting
  ) {
    const y = map.workstreamY(FAKE_WORKSTREAM_ID);
    const h = map.workstreamHeight(FAKE_WORKSTREAM_ID);
    ctx.fillStyle = ROW_FILL;
    ctx.fillRect(0, y, sidebarWidth, h);
    layout.rows.push({ top: y, bottom: y + h, fill: ROW_FILL });
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(sidebarWidth, y + 0.5);
    ctx.lineWidth = 1;
    ctx.strokeStyle = borderColor;
    ctx.stroke();
    ctx.fillStyle = textColor;
    ctx.font = '700 11px Roboto, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    // WI #5596: the row can hold manually-hidden workstreams, filter-hidden
    // activities, or a mix — say which, so the label never claims hidden
    // workstreams that aren't there. Wrapped like the workstream titles
    // below (not truncated): the longer variants would otherwise be cut to
    // "Hidden workstre…" and the distinction would be invisible.
    const label =
      io.hiddenWorkstreamsOnMapCount > 0
        ? map.hasFilterHiddenActivities
          ? $t('map.hiddenWorkstreamsAndFilter')
          : $t('map.hiddenWorkstreams')
        : $t('map.hiddenByFilter');
    const lineHeight = 14;
    const maxLines = Math.max(1, Math.floor(h / lineHeight));
    // Keep clear of the EyeOff on the left (its hit box ends at x ≈ 28).
    const lines = wrapText(ctx, label, sidebarWidth - 45, maxLines);
    const startY = y + (h - lines.length * lineHeight) / 2 + lineHeight / 2;
    lines.forEach((line, i) => {
      ctx.fillText(line, sidebarWidth - 10, startY + i * lineHeight);
    });
    if (io.hiddenWorkstreamsOnMapCount > 0) {
      const eye = workstreamEyeCenter(map, FAKE_WORKSTREAM_ID);
      drawIcon(ctx, 'EyeOff', eye.x, eye.y, EYE_SIZE, eyeColor);
    }
  }

  const iconRows: { id: string; expanded: boolean; color: string }[] = [];
  for (const ws of io.workstreams) {
    if (ws.isHiddenOnMap || map.filterHiddenWorkstreamIds.has(ws.id)) {
      continue;
    }
    const y = map.workstreamY(ws.id);
    const h = map.workstreamHeight(ws.id);
    // WI #5729: the selected workstream's row is highlighted as the SVG did;
    // its title, Info and row icons go white on it.
    const selected = map.isSelectedItem('workstream', ws.id);
    const fill = selected ? selectedFill : ROW_FILL;
    const contentColor = selected ? selectedText : textColor;

    ctx.fillStyle = fill;
    ctx.fillRect(0, y, sidebarWidth, h);
    layout.rows.push({ top: y, bottom: y + h, fill });

    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(sidebarWidth, y + 0.5);
    ctx.lineWidth = 1;
    ctx.strokeStyle = borderColor;
    ctx.stroke();

    // A description shows an Info icon at the row's right edge (MapWorkstreams'
    // UiIcon name="Info"); right-align the title just left of it so they don't
    // overlap. Hovering the icon shows MapWorkstreamTooltip (handled in the router).
    const info = ws.description ? workstreamInfoCenter(map, ws.id) : null;
    const titleRight = info ? info.x - INFO_SIZE / 2 - 5 : sidebarWidth - 10;

    ctx.fillStyle = contentColor;
    ctx.font = '700 11px Roboto, sans-serif';
    ctx.textAlign = 'right';
    const lineHeight = 14;
    const maxLines = Math.max(1, Math.floor(h / lineHeight));
    const lines = wrapText(ctx, ws.title, titleRight - 10, maxLines);
    const totalHeight = lines.length * lineHeight;
    const startY = y + (h - totalHeight) / 2 + lineHeight / 2;
    ctx.textBaseline = 'middle';
    const widths = lines.map((line) => ctx.measureText(line).width);
    lines.forEach((line, i) => {
      const lineY = startY + i * lineHeight;
      ctx.fillText(line, titleRight, lineY);
      layout.titleLines.push({
        y: lineY,
        left: titleRight - widths[i],
        rowTop: y,
      });
    });

    // WI #5550: record the drawn (wrapped, right-aligned) title bounds so the
    // pointer affordance covers exactly the text. No lines → no region.
    if (lines.length > 0) {
      const widest = Math.max(...widths);
      scene.hits.addWorkstreamTitle(ws.id, {
        x: titleRight - widest - TITLE_HIT_PAD,
        y: startY - lineHeight / 2 - TITLE_HIT_PAD,
        w: widest + 2 * TITLE_HIT_PAD,
        h: lines.length * lineHeight + 2 * TITLE_HIT_PAD,
      });
    }

    if (info) {
      drawIcon(ctx, 'Info', info.x, info.y, INFO_SIZE, contentColor);
    }

    // WI #5563: the expanded (solo) workstream shows a persistent collapse
    // button (always visible, so the user can always escape) and no hide-eye
    // (canHideWorkstream is false when it is the sole visible row). Every other
    // row shows the hide-eye plus an expand button, revealed only on hover.
    const iconColor = selected ? selectedText : eyeColor;
    if (io.expandedWorkstreamId === ws.id) {
      iconRows.push({ id: ws.id, expanded: true, color: iconColor });
    } else if (
      canHideWorkstream(io, ws.id) &&
      scene.state.hoverWorkstreamId === ws.id
    ) {
      iconRows.push({ id: ws.id, expanded: false, color: iconColor });
    }
  }

  // WI #5729: the row-top icons go on after every row — in a short row they
  // reach into the row below, which would otherwise paint over them — and on a
  // backdrop that hides the title text beneath them while they show. They are
  // interactive, so the PDF export leaves them out, like the placeholder row.
  if (!scene.exporting) {
    for (const row of iconRows) {
      const expand = workstreamExpandCenter(map, row.id);
      if (row.expanded) {
        drawIconBackdrop(ctx, layout, expand);
        drawIcon(ctx, 'Minimize2', expand.x, expand.y, EYE_SIZE, row.color);
      } else {
        // Hide-eye (MapWorkstreams' Eye → toggleWorkstreamVisibility). One
        // backdrop spans both icons, so no text peeks out between them.
        const eye = workstreamEyeCenter(map, row.id);
        drawIconBackdrop(ctx, layout, eye, expand);
        drawIcon(ctx, 'Eye', eye.x, eye.y, EYE_SIZE, row.color);
        drawIcon(ctx, 'Maximize2', expand.x, expand.y, EYE_SIZE, row.color);
      }
    }
  }

  // Gap dividers at each slot where a run of workstreams was hidden: the double
  // line is always shown; its EyeOff (re-show the run) reveals on hover.
  const gaps = hiddenWorkstreamGaps(io, map.filterHiddenWorkstreamIds);
  const hoverGap =
    scene.state.hoverGapIndex === null
      ? undefined
      : gaps[scene.state.hoverGapIndex];
  const gapEye = hoverGap ? gapEyeCenter(map, io, hoverGap) : null;
  // WI #5729: the EyeOff's backdrop goes down before every double line, so it
  // covers the title text beneath the icon but none of the dividers.
  if (gapEye) {
    drawIconBackdrop(ctx, layout, gapEye);
  }
  // The gap divider double-line uses primary-500 @ 0.6 (MapWorkstreams `.gap-line`),
  // not the gray-300 row border.
  const gapColor = cssVar('--color-primary-500', '#2563EB');
  for (const gap of gaps) {
    const [y1, y2] = gapLineYs(map, io, gap);
    ctx.save();
    ctx.globalAlpha = 0.6;
    ctx.strokeStyle = gapColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, y1 + 0.5);
    ctx.lineTo(sidebarWidth, y1 + 0.5);
    ctx.moveTo(0, y2 + 0.5);
    ctx.lineTo(sidebarWidth, y2 + 0.5);
    ctx.stroke();
    ctx.restore();
  }
  if (gapEye) {
    drawIcon(ctx, 'EyeOff', gapEye.x, gapEye.y, EYE_SIZE, eyeColor);
  }

  // Scroll shadow at the sidebar's right edge (SVG: workstream-shadow @ offset>20).
  if (map.offset > 20) {
    const grad = ctx.createLinearGradient(
      sidebarWidth,
      0,
      sidebarWidth + 20,
      0,
    );
    grad.addColorStop(0, 'rgba(0, 0, 0, 0.08)');
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(sidebarWidth, START_Y - 140, 20, scene.height);
  }
}

/**
 * WI #5729: repaint the sidebar in one box behind the icons centred at
 * `centres` (all on one row), ±ICON_BACKDROP_HALF around each: every row's own
 * fill, then the row borders through the box, so only the title text beneath
 * goes. A title line the box would cut through is covered whole instead, so no
 * sliver of its glyphs shows above or below the box.
 */
function drawIconBackdrop(
  ctx: CanvasRenderingContext2D,
  layout: SidebarLayout,
  ...centres: { x: number; y: number }[]
): void {
  const xs = centres.map((c) => c.x);
  const ys = centres.map((c) => c.y);
  const left = Math.min(...xs) - ICON_BACKDROP_HALF;
  const right = Math.max(...xs) + ICON_BACKDROP_HALF;
  let top = Math.min(...ys) - ICON_BACKDROP_HALF;
  let bottom = Math.max(...ys) + ICON_BACKDROP_HALF;
  for (const line of layout.titleLines) {
    const inkTop = line.y - TITLE_INK_ABOVE;
    const inkBottom = line.y + TITLE_INK_BELOW;
    if (line.left < right && inkTop < bottom && inkBottom > top) {
      top = Math.min(top, Math.max(inkTop, line.rowTop + 1));
      bottom = Math.max(bottom, inkBottom);
    }
  }
  for (const row of layout.rows) {
    const from = Math.max(top, row.top);
    const to = Math.min(bottom, row.bottom);
    if (from < to) {
      ctx.fillStyle = row.fill;
      ctx.fillRect(left, from, right - left, to - from);
    }
  }
  // A row's top border is the 1 px line at row.top.
  const borders = layout.rows.filter(
    (row) => row.top < bottom && row.top + 1 > top,
  );
  if (borders.length > 0) {
    ctx.beginPath();
    for (const row of borders) {
      ctx.moveTo(left, row.top + 0.5);
      ctx.lineTo(right, row.top + 0.5);
    }
    ctx.lineWidth = 1;
    ctx.strokeStyle = layout.borderColor;
    ctx.stroke();
  }
}

/**
 * Draw the project header image over the sidebar pre-block (MapWorkstreams'
 * `<image>`): projects only, box `START_X − 30 × START_Y` at the top-left. The
 * blob URL is loaded by `projectStore.loadProjectImage` (triggered once in
 * MapCanvas, mirroring the SVG `onMounted`); we read the resolved URL and let the
 * ImageCache fetch/decode the bitmap, repainting when it lands. The bitmap is
 * letterboxed (contain) to match the SVG `<image>` default `xMidYMid meet`.
 */
function drawProjectImage(
  ctx: CanvasRenderingContext2D,
  scene: SceneContext,
): void {
  const { map, project } = scene;
  if (map.isTemplate) {
    return;
  }
  const url =
    project.imageUrls[map.mapId] ||
    project.project(map.mapId)?.project_image_url;
  if (!url) {
    return;
  }
  const img = scene.images.get(url);
  if (!img) {
    return;
  }
  const boxW = START_X - 30;
  const boxH = START_Y;
  // Contain fit (preserve aspect, centre) — SVG `<image>` default meet.
  const scale = Math.min(boxW / img.naturalWidth, boxH / img.naturalHeight);
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  ctx.drawImage(img, (boxW - w) / 2, (boxH - h) / 2, w, h);
}
